/**
 * The GST portal's two ledger balances on the 3B screen (C-05, C-06; FR-E20, FR-E21).
 *
 * Shows each ledger's balance — or "Not entered" — with where it came from (R-E5: the
 * portal's download, the API, or a person typing it), and the difference when the balance
 * used disagrees with the one carried forward from last month's filed return. What 6.1's
 * cash leaves to deposit is 6.1's own table, below it. For `period.can_update`, a card to
 * type a balance off the portal with a required note, or to clear one back to not entered.
 *
 * Its own file, as `Gstr3bTypedFigure.tsx` is, so the lifecycle's edits to `Gstr3b.tsx`
 * stay mergeable.
 */

import { Alert, Button, Card, Textarea } from '@fiveox/ui'
import { useState } from 'react'

import { ApiError, periods } from '../lib/api'
import { paiseFromRupees } from '../lib/gstr3bForm'
import {
  LEDGER_HEADS,
  LEDGER_LABELS,
  type Ledgers,
  differenceWords,
  ledgerSourceWords,
  typedDifferenceWords,
} from '../lib/gstr3bLedgers'

const HEAD_LABELS: Record<string, string> = {
  igst: 'Integrated tax',
  cgst: 'Central tax',
  sgst: 'State/UT tax',
  cess: 'Cess',
}

function rupees(paise: number | undefined): string {
  return ((paise ?? 0) / 100).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

export function Gstr3bLedgers({
  periodId,
  ledgers,
  canType,
  onSaved,
}: {
  periodId: string
  ledgers: Ledgers
  canType: boolean
  onSaved: () => void | Promise<void>
}) {
  const credit = ledgers?.credit ?? null
  const cash = ledgers?.cash ?? null
  const difference = differenceWords(credit)
  /** ND-E5: where the portal's balance is used and a typed one disagrees, each said. */
  const fromTyped = [typedDifferenceWords(credit), typedDifferenceWords(cash)].filter(
    (w): w is string => !!w,
  )

  return (
    <div className="mb-5">
      <p className="gst-panel-h mb-1">Balances from the GST portal</p>
      <div className="gst-table-wrap mb-2">
        <table className="gst-table">
          <thead>
            <tr>
              <th className="is-text">Ledger</th>
              {LEDGER_HEADS.map((h) => (
                <th key={h}>{HEAD_LABELS[h]}</th>
              ))}
              <th className="is-text">Where it came from</th>
            </tr>
          </thead>
          <tbody>
            {(
              [
                ['credit', credit],
                ['cash', cash],
              ] as const
            ).map(([key, balance]) => (
              <tr key={key}>
                <td className="is-text is-sec">{LEDGER_LABELS[key]}</td>
                {balance ? (
                  LEDGER_HEADS.map((h) => <td key={h}>{rupees(balance.heads[h])}</td>)
                ) : (
                  <td colSpan={LEDGER_HEADS.length} className="is-text">
                    Not entered
                  </td>
                )}
                <td className="is-text gst-meta">{ledgerSourceWords(balance)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {difference && (
        <Alert tone="warning" className="mb-2">
          {difference}
        </Alert>
      )}
      {fromTyped.map((words) => (
        <Alert key={words} tone="warning" className="mb-2">
          {words}
        </Alert>
      ))}
      {canType && <LedgerBalanceCard periodId={periodId} onSaved={onSaved} />}
    </div>
  )
}

function LedgerBalanceCard({
  periodId,
  onSaved,
}: {
  periodId: string
  onSaved: () => void | Promise<void>
}) {
  const [ledger, setLedger] = useState<'credit' | 'cash'>('credit')
  const [amounts, setAmounts] = useState<Record<string, string>>({})
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [message, setMessage] = useState<{ tone: 'error' | 'success'; text: string } | null>(
    null,
  )

  const heads: Record<string, number> = {}
  let unreadable: string | null = null
  for (const head of LEDGER_HEADS) {
    const paise = paiseFromRupees(amounts[head] ?? '')
    if (paise === null) unreadable = HEAD_LABELS[head] ?? head
    else heads[head] = paise
  }

  const save = async (clear: boolean) => {
    setSaving(true)
    try {
      const saved = await periods.typeLedgerBalance(periodId, {
        ledger,
        heads: clear ? null : heads,
        note,
      })
      setMessage({
        tone: 'success',
        text:
          `${LEDGER_LABELS[ledger]}: ${clear ? 'cleared — now not entered' : 'balance recorded with its note'}.` +
          (saved.data.demoted_periods.length > 0
            ? ' The GSTR-3B was past preparation and has gone back for preparing again.'
            : ''),
      })
      setAmounts({})
      setNote('')
      await onSaved()
    } catch (err) {
      setMessage({
        tone: 'error',
        text: err instanceof ApiError ? err.message : 'The balance could not be recorded.',
      })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Card className="p-4">
      <p className="gst-panel-h mb-1">Type a balance from the GST portal</p>
      <p className="gst-meta mb-3">
        Read the balance off the portal's electronic ledger before filing, and type it per head
        with a note saying where and when. The credit balance is the opening credit for 6.1; the
        cash balance pays 6.1's cash before any deposit.
      </p>
      {message && (
        <Alert tone={message.tone} className="mb-3">
          {message.text}
        </Alert>
      )}
      <div className="flex flex-wrap items-end gap-2">
        <label className="gst-label">
          Ledger
          <select
            className="gst-select"
            value={ledger}
            onChange={(e) => setLedger(e.target.value === 'cash' ? 'cash' : 'credit')}
            aria-label="The ledger being typed"
          >
            <option value="credit">{LEDGER_LABELS.credit}</option>
            <option value="cash">{LEDGER_LABELS.cash}</option>
          </select>
        </label>
        {LEDGER_HEADS.map((head) => (
          <label key={head} className="gst-label">
            {HEAD_LABELS[head]} (₹)
            <input
              className="gst-input w-32"
              inputMode="decimal"
              placeholder="0.00"
              value={amounts[head] ?? ''}
              onChange={(e) => setAmounts({ ...amounts, [head]: e.target.value })}
            />
          </label>
        ))}
      </div>
      <div className="mt-2">
        <Textarea
          label="Note — where on the portal, and when (required)"
          rows={2}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      {unreadable && <p className="gst-meta mt-1">{unreadable} is not an amount in rupees and paise.</p>}
      <div className="mt-2 flex gap-2">
        <Button
          type="button"
          size="sm"
          disabled={saving || !note.trim() || unreadable !== null}
          onClick={() => void save(false)}
        >
          {saving ? 'Recording…' : 'Record the balance'}
        </Button>
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={saving || !note.trim()}
          onClick={() => void save(true)}
        >
          Clear it to not entered
        </Button>
      </div>
    </Card>
  )
}
